/** @jsxImportSource @emotion/react */

import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import * as color from '@constant/color';
import * as size from '@constant/size';
import IconTextBlock from '@component/common/block/icon-text-block';
import ImageBlock from '@component/common/block/image-block';
import TextButton from '@design-system/text-button';
import Canvas from '@design-system/canvas';
import Callout from '@design-system/callout';
import Gap from '@component/common/gap';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/modal-slice';
import { actions as navigationActions } from '@store/slice/navigation-slice';
import { actions as workbookActions } from '@store/slice/workbook-slice';
import { actions as classroomActions } from '@store/slice/classroom-slice';
import { IYAGIGO_PATH, createClozePath } from '@constant/iyagigo/route-path';
import { ISubscriptionWallToastData } from '@datatype/iyagigo/type';
import { ETheme } from '@datatype/type';
import otterReadImage from '@asset/image/otter-read.png';
import { useCheckAttendance } from '@hook/iyagigo/useStudent';
import { getAttendanceType } from '@util/attendance';

interface IProps {
  data: ISubscriptionWallToastData['data'];
}

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: 100%;
`;

const OtterMessageContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

const FeatureContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

const FEATURE_DESIGN = {
  text: { size: 'small', color: color.BLACK_2, bold: false },
  icon: { size: 'small', color: color.BROWN_1, kind: 'filled' },
  box: { alignX: 'left', gap: '6px' },
} as const;

const SubscriptionWallToast: React.FC<IProps> = ({ data }) => {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const { trigger, attendanceType, feature, quota } = data;
  const checkAttendance = useCheckAttendance();
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const mainMenu = useTypedSelector((state) => state.rootReducer?.navigationReducer?.iyagigo?.mainMenu);
  const subMenu = useTypedSelector((state) => state.rootReducer?.navigationReducer?.iyagigo?.subMenu);

  useEffect(() => {
    if (trigger !== 'quota' || feature === 'speaking') return;
    checkAttendance(
      attendanceType ??
        getAttendanceType(studyLanguage, mainMenu === 'vocabulary-book' ? 'vocabulary-book' : subMenu, mainMenu),
    );
  }, [attendanceType, trigger, feature]);

  const handlePremiumClick = () => {
    dispatch(modalActions.hideOverlay());
    dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu: 'hub', subMenu: 'subscription' }));
    navigate(IYAGIGO_PATH.HUB_SUBSCRIPTION_PATH);
  };

  const handleQuitClick = () => {
    dispatch(modalActions.hideToast());
    // quota: 학습 종료 + today-home 이동 (한도 도달 → 더 못 풀이)
    // access: 닫기만 (사용자가 보던 화면 유지)
    if (trigger === 'quota' && feature !== 'speaking') {
      dispatch(workbookActions.storeIsStudying(false));
      dispatch(classroomActions.storeIsStudying(false));
      navigate(createClozePath('home'));
    }
  };

  const getCalloutText = () => {
    if (trigger !== 'quota') return 'Plus⁺에서 쓸 수 있어요\n구독하고 바로 써보세요';

    const description = '\n<strong>월 ₩3,250</strong>으로 이어서 학습해요';
    switch (feature) {
      case 'speaking':
        return `오늘 무료 대화 3회를\n모두 채웠어요${description}`;
      case 'cloze':
        if (quota != null) return `오늘 무료 ${quota}문제를\n모두 채웠어요${description}`;
        return `오늘 무료 학습 끝!${description}`;
      default:
        return `오늘 무료 학습 끝!${description}`;
    }
  };

  return (
    <Wrapper>
      <OtterMessageContainer>
        <ImageBlock src={otterReadImage} alt='이야기고' design={{ image: { height: '80px' } }} />
        <Callout text={getCalloutText()} direction='left' theme={ETheme.CREAM} size='medium' width='fit-content' />
      </OtterMessageContainer>
      <Gap height={size.MARGIN_1} />
      <Canvas theme={ETheme.BROWN} shape='square' width='100%' padding={size.MARGIN_1}>
        <FeatureContainer>
          <IconTextBlock icon='check-box' text='더 많은 말하기 연습' design={FEATURE_DESIGN} />
          <IconTextBlock icon={'check-box'} text={'더 많은 빈칸문제 학습'} design={FEATURE_DESIGN} />
          <IconTextBlock icon={'check-box'} text={'더 많은 단어집 학습'} design={FEATURE_DESIGN} />
          {/* 종료된 서비스: <IconTextBlock icon={'check-box'} text={'모든 표현·이야기'} design={FEATURE_DESIGN} /> */}
          <IconTextBlock icon={'check-box'} text={'나만의 단어집·문제집·반복학습'} design={FEATURE_DESIGN} />
        </FeatureContainer>
      </Canvas>
      <Gap height={size.MARGIN_2} />
      <TextButton
        theme={ETheme.BROWN}
        kind={'filled'}
        size={'medium'}
        shape='pill'
        width={'100%'}
        text={'이야기고 Plus⁺ 시작하기'}
        handleClick={handlePremiumClick}
      />
    </Wrapper>
  );
};

export default SubscriptionWallToast;
